import { $t } from '@/i18n';
import { useRouter } from '@/router/useRouter';
import { createAiProjectInfoExclude, hasCompleteArtifactCos, resolveArtifactCosToArtifact, shouldUseSlimAiProjectInfo } from '@/utils/aiProjectInfo';
import getXyManagePathPrefix from '@/utils/getXyManagePathPrefix';
import { getAppQuery } from '@/utils/path';
import wujiFetch from '@/utils/wujiFetch';
import { gogoBackendStatus } from './ai-project/backend-status/store';
import dayjs from 'dayjs';
import { message } from 'tdesign-vue';
import { isEmpty } from 'lodash';
import { gzipAssetFileContentsToBase64, unifyAssetFileContents } from './unifyAssetFileContents';
const getProjectId = () => useRouter()?.route?.value?.query?.projectid;
const getBranchId = () => useRouter()?.route?.value?.query?.branch;
// 将通用的http请求函数统一为wujiFetch，保持与项目其他部分一致
const http = async (url, options = {}) => {
    const response = await wujiFetch(url, options);
    return response;
};
const getApiBase = (envId = 'dev', branchId = getBranchId()) => `${getXyManagePathPrefix({ envId, branchId })}/ai-project`;
const createProjectInfoUrl = ({ projectId, envId = 'dev', branchId = getBranchId(), excludeArtifact = false }) => {
    const params = new URLSearchParams({ projectId: projectId || getProjectId() });
    params.set('exclude', createAiProjectInfoExclude({ excludeArtifact }));
    return `${getApiBase(envId, branchId)}/info?${params.toString()}`;
};
const requestProjectInfo = async ({ projectId, envId = 'dev', branchId = getBranchId(), excludeArtifact = false } = {}) => await http(createProjectInfoUrl({
    projectId,
    envId,
    branchId,
    excludeArtifact,
}), {
    method: 'GET',
});
const resolveProjectInfoFromArtifactCos = async (projectInfo) => {
    const artifact = await resolveArtifactCosToArtifact(projectInfo?.artifactCos);
    return {
        ...projectInfo,
        artifact,
    };
};
export const gogoBackendBuildAPI = {
    getBuildLog: async (projectId, date, options = {}) => {
        const resp = await http(`${getApiBase()}/backend/build-log?projectId=${projectId}&date=${date}`, {
            timeout: options.timeout,
        });
        return resp;
    },
    getBuildLogStream: async (projectId, date) => {
        const res = await http(`${getApiBase()}/backend/build-log?projectId=${projectId}&date=${date}&stream=true`, {
            timeout: 15e3,
            rawResponse: true,
        });
        if (!res.headers.get('content-type')?.includes('x-ndjson')) {
            // 服务器侧支持有问题，是旧版，直接解析
            const resp = await res.json();
            if (resp.code !== 200)
                throw new Error(`bad response: ${resp.error}`);
            // mock a stream, res.data is string[]
            return new ReadableStream({
                start(controller) {
                    for (const item of resp.data || []) {
                        const line = typeof item === 'string' ? item : JSON.stringify(item);
                        if (line.trim())
                            controller.enqueue(line);
                    }
                    controller.close();
                },
            });
        }
        let rest = '';
        return res.body
            .pipeThrough(new TextDecoderStream())
            .pipeThrough(new TransformStream({
            transform(chunk, controller) {
                const lines = `${rest}${chunk}`.split(/\r?\n/);
                rest = lines.pop() || '';
                for (const line of lines) {
                    if (line.trim())
                        controller.enqueue(line);
                }
            },
            flush(controller) {
                if (rest.trim())
                    controller.enqueue(rest);
            },
        }));
    },
    getRuntimeLog: async ({ projectId, envId = 'dev', branchId = getBranchId(), since, tail, cursor, count }) => {
        const params = new URLSearchParams({ projectId });
        if (since)
            params.set('since', since);
        if (tail && tail > 0)
            params.set('tail', String(tail));
        if (cursor)
            params.set('cursor', cursor);
        if (count && count > 0)
            params.set('count', String(count));
        const resp = await http(`${getApiBase(envId, branchId)}/backend/runtime-log?${params}`);
        if (Array.isArray(resp))
            return { logs: resp }; // 旧版 API 返回的是数组
        return resp;
    },
    stopService: ({ projectId, envId = 'dev', branchId = getBranchId() }) => http(`${getApiBase(envId, branchId)}/backend/stop-service?projectId=${projectId}`, { method: 'POST', body: '{}' }),
    forceRebuildBackend: async (options = {}) => {
        const params = new URLSearchParams({ projectId: getProjectId() });
        const resp = await http(`${getApiBase()}/backend/force-rebuild?${params.toString()}`, {
            method: 'POST',
            body: JSON.stringify({ clearBunLock: !!options.clearBunLock }),
        });
        if (!resp.success) {
            message.error('Failed to force rebuild backend');
            console.error('Failed to force rebuild backend', resp);
            return null;
        }
        gogoBackendStatus.reset(getProjectId(), resp.status, resp.backendInfo);
        return resp;
    },
};
export const projectAPI = {
    async getBackendServiceStatus(projectId) {
        const currentProjectId = projectId || getProjectId();
        // poll status 的工作在 ./ai-project/backend-status/store.tsx 中
        if (gogoBackendStatus.status === 'deploying')
            return { status: 'updating' };
        if (gogoBackendStatus.status === 'deployed')
            return { status: 'ready' };
        if (gogoBackendStatus.status === 'failed')
            return {
                status: 'error',
                buildLog: gogoBackendStatus.buildLog
                    .filter(x => x.type === 'log')
                    .map(x => x.message)
                    .join('\n'),
            };
        void currentProjectId;
        return null;
    },
    async getBackendServiceRunningLogs(options) {
        const projectId = getProjectId();
        try {
            const ans = await gogoBackendBuildAPI.getRuntimeLog({ projectId, tail: options.tail });
            return ans.logs.map(x => `${dayjs(x.timestamp).format('YYYY-MM-DD HH:mm:ss')} | ${x.message}`);
        }
        catch (e) {
            console.error('getBackendServiceRunningLogs error', e);
            return null;
        }
    },
    async initializeProject(projectId, body) {
        const params = new URLSearchParams({ projectId: projectId || getProjectId() });
        await http(`${getApiBase()}/initialize?${params.toString()}`, {
            method: 'POST',
            body: body ? JSON.stringify(body) : '{}',
            headers: {
                'Content-Type': 'application/json',
            },
        });
        return true;
    },
    async getProjectInfo(opts) {
        let data;
        let projectId; // 历史代码留下的，实际应该没用到
        const editorEnv = getAppQuery('env') || 'dev';
        if (opts?.disallowSlimAiProjectInfo || !shouldUseSlimAiProjectInfo({ env: editorEnv, branchId: getBranchId() })) {
            data = await requestProjectInfo({ projectId });
        }
        else {
            const slimProjectInfo = await requestProjectInfo({ projectId, excludeArtifact: true });
            if (hasCompleteArtifactCos(slimProjectInfo)) {
                try {
                    data = await resolveProjectInfoFromArtifactCos(slimProjectInfo);
                }
                catch (error) {
                    console.error('[xy-client] resolve ai artifactCos failed in editor stage', error);
                    data = await requestProjectInfo({ projectId });
                }
            }
            else {
                data = await requestProjectInfo({ projectId });
            }
        }
        gogoBackendStatus.reset(getProjectId(), data?.backendDeployStatus || '', data?.backendInfo || null);
        return data;
    },
    async updateProjectInfo(projectInfo) {
        const payload = { ...projectInfo };
        if (payload.artifact) {
            // artifact 字段的格式，wuji 存储的和 gogo-editor 的有微妙差别
            const artifact = { ...payload.artifact };
            payload.artifact = artifact;
            if (!isEmpty(artifact.assetFiles)) {
                // 注意 gogo editor 输出带 Uint8Array 这里统一转一下 { contentGzipBase64: string }
                // 见 server src/Modules/LowCode/types/AiProject.ts
                const assetFileEntryPromises = Object.entries(artifact.assetFiles).map(async ([path, file]) => {
                    const content = await unifyAssetFileContents(file) || new Uint8Array();
                    return [path, { contentGzipBase64: await gzipAssetFileContentsToBase64(content) }];
                });
                artifact.assetFiles = Object.fromEntries(await Promise.all(assetFileEntryPromises));
            }
        }
        const params = new URLSearchParams({ projectId: getProjectId() });
        await http(`${getApiBase()}/info?${params.toString()}`, {
            method: 'POST',
            body: JSON.stringify(payload),
            headers: {
                'Content-Type': 'application/json',
            },
        });
        this.getProjectInfo(); // 后台拉取
        return true;
    },
};
export const conversationAPI = {
    async listConversations() {
        const projectId = encodeURIComponent(getProjectId());
        const data = await http(`${getApiBase()}/conv?projectId=${projectId}`, {
            method: 'GET',
        });
        return Array.isArray(data) ? data : [];
    },
    async getConversation(conversationId) {
        const data = await http(`${getApiBase()}/conv/${encodeURIComponent(conversationId)}?projectId=${getProjectId()}`, {
            method: 'GET',
        });
        return data;
    },
    async upsertConversation(conversation) {
        let data = conversation;
        if ('messages' in conversation) {
            const copy = { ...conversation };
            delete copy.messages;
            data = copy;
        }
        await http(`${getApiBase()}/conv/${encodeURIComponent(conversation.id)}?projectId=${getProjectId()}`, {
            method: 'POST',
            body: JSON.stringify(data),
            headers: {
                'Content-Type': 'application/json',
            },
        });
        return true;
    },
    async upsertMessage(message) {
        await http(`${getApiBase()}/msg/${encodeURIComponent(message.id)}?projectId=${getProjectId()}`, {
            method: 'POST',
            body: JSON.stringify(message),
            headers: {
                'Content-Type': 'application/json',
            },
        });
        return true;
    },
    async batchUpsertMessages(messages) {
        await http(`${getApiBase()}/msg/batch?projectId=${getProjectId()}`, {
            method: 'POST',
            body: JSON.stringify(messages),
            headers: {
                'Content-Type': 'application/json',
            },
        });
        return true;
    },
};
export const repositoryAPI = {
    async listRepoObjects(mtime) {
        const query = new URLSearchParams();
        query.set('projectId', getProjectId());
        if (typeof mtime === 'number') {
            query.set('mtime', String(mtime));
        }
        const data = await http(`${getApiBase()}/repo/objects/list?${query.toString()}`, {
            method: 'GET',
        });
        return Array.isArray(data) ? data : [];
    },
    async readRepoObjects(oids) {
        const projectId = getProjectId();
        const data = await http(`${getApiBase()}/repo/objects/batch-read?projectId=${projectId}`, {
            method: 'POST',
            body: JSON.stringify(oids),
            headers: {
                'Content-Type': 'application/json',
            },
        });
        return data;
    },
    async writeRepoObjects(params) {
        await http(`${getApiBase()}/repo/objects/batch-write?projectId=${getProjectId()}`, {
            method: 'POST',
            body: JSON.stringify(params),
            headers: {
                'Content-Type': 'application/json',
            },
        });
        return true;
    },
    async readRepoRefs(mtime) {
        const query = new URLSearchParams();
        query.set('projectId', getProjectId());
        if (typeof mtime === 'number') {
            query.set('mtime', String(mtime));
        }
        const data = await http(`${getApiBase()}/repo/refs/list?${query.toString()}`, {
            method: 'GET',
        });
        return data;
    },
    async writeRepoRefs(params) {
        const updated = await http(`${getApiBase()}/repo/refs/batch-write?projectId=${getProjectId()}`, {
            method: 'POST',
            body: JSON.stringify(params),
            headers: {
                'Content-Type': 'application/json',
            },
        });
        return Array.isArray(updated) ? updated : [];
    },
};
// --- Proxy API ---
const getProxyNetworksConfig = () => window?.GLOBAL_INFO?.gogoProxyNetworks || {};
function getProxyFetchUrl(network) {
    const config = getProxyNetworksConfig();
    if (network in config) {
        return config[network];
    }
    // 默认使用当前服务器
    return `${getApiBase()}/proxy/fetch`;
}
export const proxyAPI = {
    async fetch(request) {
        const network = request.network || 'default';
        const url = getProxyFetchUrl(network);
        return globalThis.fetch(`${url}?projectId=${getProjectId()}`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json', 'xy-powered-by': 'gogo-proxy' },
            body: JSON.stringify({
                url: request.url,
                method: request.method,
                headers: request.headers,
                body: request.body,
            }),
        });
    },
    getNetworks() {
        const config = getProxyNetworksConfig();
        const result = [{ id: 'default', label: $t('k_5f19f440') }];
        for (const key of Object.keys(config)) {
            if (key !== 'default') {
                result.push({ id: key, label: key });
            }
        }
        return result;
    },
    getProjectId() {
        return getProjectId() || '';
    },
};
export const preferenceAPI = {
    async getItem(key) {
        const params = new URLSearchParams({ key });
        const data = await http(`${getXyManagePathPrefix()}/preference/item?${params.toString()}`, { method: 'GET' });
        return data;
    },
    async setItem(key, value) {
        await http(`${getXyManagePathPrefix()}/preference/item`, {
            method: 'PUT',
            body: JSON.stringify({ key, value }),
            headers: { 'Content-Type': 'application/json' },
        });
        return true;
    },
    async removeItem(key) {
        const params = new URLSearchParams({ key });
        await http(`${getXyManagePathPrefix()}/preference/item?${params.toString()}`, {
            method: 'DELETE',
        });
        return true;
    },
};
const backendAPI = {
    conversationAPI,
    repositoryAPI,
    projectAPI,
    proxyAPI,
    preferenceAPI,
};
export default backendAPI;
export const developmentSeedDtsAPI = {
    async listDomains(projectId) {
        const pid = projectId || getProjectId();
        return http(`${getApiBase()}/development-seed/frontend/dts?projectId=${pid}`);
    },
    async getDts(domain, projectId) {
        const pid = projectId || getProjectId();
        return http(`${getApiBase()}/development-seed/frontend/dts/${encodeURIComponent(domain)}?projectId=${pid}`);
    },
};
